import { useState } from "react";
import type { Input } from "../../services/inputsApi";
import { useSuppliers } from "../../hooks/useSuppliers";
import { useSubmitSetupApproval } from "@/features/production/hooks/useSetupApprovals";
import { SETUP_APPROVAL_TYPE } from "@/features/production/services/setupApprovalApi";
import { useActiveInputTypes } from "../../hooks/useInputTypes";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input as InputComponent } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Pencil } from "lucide-react";
import { useNotification } from '@/shared/hooks/useNotification';
import { useUnitsOfMeasure } from '@/shared/hooks/useUnitsOfMeasure';
import { useActiveCropTypes } from '@/features/production/hooks/useCropTypes';
import { useCropVarietiesByCropType } from '@/features/production/hooks/useCropVarieties';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';

interface EditInputDialogProps {
  input: Input;
}

export const EditInputsDialog = ({ input }: EditInputDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: input.id,
    inputCode: input.inputCode,
    inputName: input.inputName,
    description: input.description,
    inputTypeId: input.inputTypeId,
    supplierId: input.supplierId,
    supplierProductCode: input.supplierProductCode,
    unitOfMeasure: input.unitOfMeasure,
    unitCost: input.unitCost,
    hasExpiryDate: input.hasExpiryDate,
    shelfLifeDays: input.shelfLifeDays,
    safetyInstructions: input.safetyInstructions,
    storageRequirements: input.storageRequirements,
    cropTypeId: input.cropTypeId ?? "",
    variety: input.variety ?? "",
    isActive: input.isActive,
  });

  // Heuristic for "seed-related" Inputs (SPEC §b/§f) — variety required when seed.
  const isSeedInput =
    !!input.inputTypeName && input.inputTypeName.toLowerCase().includes("seed");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const submitApproval = useSubmitSetupApproval();
  const suppliersQuery = useSuppliers();
  const inputTypesQuery = useActiveInputTypes();
  const unitsQuery = useUnitsOfMeasure();
  const cropTypesQuery = useActiveCropTypes();
  const varietiesQuery = useCropVarietiesByCropType(formData.cropTypeId);
  const notify = useNotification();
  const confirm = useConfirm();

  const dedupedUnits = unitsQuery.data
    ? Array.from(new Map(unitsQuery.data.map((u) => [u.value, u])).values())
    : [];

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value } as typeof prev;
      // Cascade resets: a new crop type invalidates the previously chosen variety.
      if (field === "cropTypeId") {
        next.variety = "";
      }
      return next;
    });
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (isSeedInput) {
      const errs: Record<string, string[]> = {};
      if (!formData.cropTypeId) errs.cropTypeId = ["Crop type is required for seed inputs."];
      if (!formData.variety.trim()) errs.variety = ["Variety is required for seed inputs."];
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        return;
      }
    }

    // Coerce unitOfMeasure to a number for the wire — the form holds the raw
    // select value (string) and the catalog row's numeric enum.
    // Coerce empty-string GUID fields to null so the backend Guid? deserializer
    // doesn't throw a JsonException on an empty string.
    const payload = {
      ...formData,
      unitOfMeasure:
        typeof formData.unitOfMeasure === "string" && formData.unitOfMeasure !== ""
          ? Number(formData.unitOfMeasure)
          : (formData.unitOfMeasure ?? 0),
      cropTypeId: formData.cropTypeId || null,
      supplierId: formData.supplierId || null,
    };

    confirm({
      title: "Submit changes for approval?",
      description: `Changes to “${input.inputName}” will be submitted for GM approval before they take effect.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitApproval
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.Input,
            entityJson: JSON.stringify(payload),
            existingEntityId: input.id,
            description: `Update Input: ${input.inputName}`,
          })
          .then(() => {
            notify.success("Submitted for GM approval");
            setOpen(false);
          })
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            } else {
              notify.error(error.response?.data?.message ?? "Failed to submit for approval");
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      </DialogTrigger>
      <DialogContent
        
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]"
      >
        <DialogHeader>
          <DialogTitle>Edit Input</DialogTitle>
          <DialogDescription>
            Update input/product information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {submitApproval.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {submitApproval.error.message}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="inputCode">Input Code</FieldLabel>
                <InputComponent
                  id="inputCode"
                  value={formData.inputCode}
                  onChange={(e) => handleChange("inputCode", e.target.value)}
                  disabled={submitApproval.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="inputName">Input Name</FieldLabel>
                <InputComponent
                  id="inputName"
                  value={formData.inputName}
                  onChange={(e) => handleChange("inputName", e.target.value)}
                  disabled={submitApproval.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="inputType">Input Type</FieldLabel>
                <select
                  id="inputType"
                  value={formData.inputTypeId}
                  onChange={(e) => handleChange("inputTypeId", e.target.value)}
                  disabled={submitApproval.isPending || inputTypesQuery.isLoading}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select Input Type</option>
                  {inputTypesQuery.data?.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="supplierId">Supplier</FieldLabel>
                <select
                  id="supplierId"
                  value={formData.supplierId}
                  onChange={(e) => handleChange("supplierId", e.target.value)}
                  disabled={
                    submitApproval.isPending || suppliersQuery.isLoading
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {suppliersQuery.data?.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.supplierName}
                    </option>
                  ))}
                </select>
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="description">Description</FieldLabel>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  disabled={submitApproval.isPending}
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="isActive">Status</FieldLabel>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      handleChange("isActive", checked)
                    }
                    disabled={submitApproval.isPending}
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Active
                  </Label>
                </div>
              </Field>
            </div>
          </div>

          {/* Supplier & Unit Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Supplier & Unit Details
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="supplierProductCode">
                  Supplier Product Code
                </FieldLabel>
                <InputComponent
                  id="supplierProductCode"
                  value={formData.supplierProductCode}
                  onChange={(e) =>
                    handleChange("supplierProductCode", e.target.value)
                  }
                  disabled={submitApproval.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="unitOfMeasure">Unit of Measure</FieldLabel>
                <select
                  id="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={(e) =>
                    handleChange("unitOfMeasure", e.target.value)
                  }
                  disabled={submitApproval.isPending || unitsQuery.isLoading}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select Unit</option>
                  {dedupedUnits.map((unit) => (
                    <option key={unit.value} value={unit.value.toString()}>
                      {unit.name} {unit.abbreviation ? `(${unit.abbreviation})` : ''}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="unitCost">Unit Cost</FieldLabel>
                <InputComponent
                  id="unitCost"
                  type="number"
                  step="0.01"
                  value={formData.unitCost}
                  onChange={(e) =>
                    handleChange("unitCost", parseFloat(e.target.value) || 0)
                  }
                  disabled={submitApproval.isPending}
                />
              </Field>

              {isSeedInput && (
                <Field>
                  <FieldLabel htmlFor="cropTypeId">
                    Crop Type <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Select
                    value={formData.cropTypeId}
                    onValueChange={(value) => handleChange("cropTypeId", value)}
                    disabled={submitApproval.isPending || cropTypesQuery.isLoading}
                  >
                    <SelectTrigger id="cropTypeId" className="w-full">
                      <SelectValue
                        placeholder={cropTypesQuery.isLoading ? "Loading..." : "Select Crop Type"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {cropTypesQuery.data?.map((ct) => (
                        <SelectItem key={ct.id} value={ct.id}>
                          {ct.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.cropTypeId && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.cropTypeId.join(", ")}
                    </div>
                  )}
                </Field>
              )}

              <Field className={isSeedInput ? "" : "col-span-2"}>
                <FieldLabel htmlFor="variety">
                  Variety {isSeedInput && <span className="text-red-500">*</span>}
                </FieldLabel>
                {isSeedInput ? (
                  <Select
                    value={formData.variety}
                    onValueChange={(value) => handleChange("variety", value)}
                    disabled={
                      submitApproval.isPending ||
                      !formData.cropTypeId ||
                      varietiesQuery.isLoading
                    }
                  >
                    <SelectTrigger id="variety" className="w-full">
                      <SelectValue
                        placeholder={
                          !formData.cropTypeId
                            ? "Pick a crop type first"
                            : varietiesQuery.isLoading
                            ? "Loading varieties..."
                            : (varietiesQuery.data ?? []).length === 0
                            ? "No varieties for this crop type"
                            : "Select Variety"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(varietiesQuery.data ?? []).map((v) => (
                        <SelectItem key={v.id} value={v.name}>
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <InputComponent
                    id="variety"
                    value={formData.variety}
                    onChange={(e) => handleChange("variety", e.target.value)}
                    disabled={submitApproval.isPending}
                    placeholder="Optional"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {isSeedInput
                    ? formData.cropTypeId &&
                      !varietiesQuery.isLoading &&
                      (varietiesQuery.data ?? []).length === 0
                      ? "No varieties yet for this crop type. Add one in Crop Varieties first."
                      : "Receivers see this variety on every line of the receive form."
                    : "Optional varietal label for fertilizers, growing media, etc."}
                </p>
                {fieldErrors.variety && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.variety.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Expiry & Safety */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Expiry & Safety</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasExpiryDate"
                  checked={formData.hasExpiryDate}
                  onCheckedChange={(checked) =>
                    handleChange("hasExpiryDate", checked)
                  }
                  disabled={submitApproval.isPending}
                />
                <Label htmlFor="hasExpiryDate" className="cursor-pointer">
                  Has Expiry Date
                </Label>
              </div>

              {formData.hasExpiryDate && (
                <Field>
                  <FieldLabel htmlFor="shelfLifeDays">
                    Shelf Life (Days)
                  </FieldLabel>
                  <InputComponent
                    id="shelfLifeDays"
                    type="number"
                    value={formData.shelfLifeDays}
                    onChange={(e) =>
                      handleChange(
                        "shelfLifeDays",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    disabled={submitApproval.isPending}
                  />
                </Field>
              )}

              <Field>
                <FieldLabel htmlFor="safetyInstructions">
                  Safety Instructions
                </FieldLabel>
                <textarea
                  id="safetyInstructions"
                  value={formData.safetyInstructions}
                  onChange={(e) =>
                    handleChange("safetyInstructions", e.target.value)
                  }
                  disabled={submitApproval.isPending}
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="storageRequirements">
                  Storage Requirements
                </FieldLabel>
                <textarea
                  id="storageRequirements"
                  value={formData.storageRequirements}
                  onChange={(e) =>
                    handleChange("storageRequirements", e.target.value)
                  }
                  disabled={submitApproval.isPending}
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitApproval.isPending}
          >
            {submitApproval.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
